import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import { drawerSections, initialValues } from './content';
import { modifyMotor, getModelsByBrand } from './queries';
import { IMotorDetailsProps } from '.';
import { getError, lookupListAsRecordObject } from '@utils/graph-utils';
import { mapDataToFormValues } from './utils';
import { isEmpty } from '@utils/validationUtils';
import DataService from '@services/DataService';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { IMotorLovs } from '..';
import { IAbstractRecord } from '@models';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { ISelectFormFieldProps } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

const MotorDetailsDrawer: React.FC<IMotorDetailsProps> = ({
  open,
  onSuccess,
  onClose,
  generalData,
  data,
  lovs: incomingLovs,
}) => {
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [values, setValues] = useState<IAbstractRecord>(initialValues);
  const [lovs, setLovs] = useState<IMotorLovs>(incomingLovs);
  const [loadingTransliteration, setLoadingTransliteration] = useState(false);

  const [fetchModelsByBrand, { loading: loadingModels }] =
    useLazyQuery(getModelsByBrand());
  const [saveMotorDetails] = useMutation(modifyMotor());

  const initialize = async () => {
    try {
      setLoading(true);
      if (data) {
        const mappedValues = mapDataToFormValues(data);
        setValues(mappedValues);
        const brand = data.make.Id;
        if (brand) {
          await loadModels(brand);
        }
      } else {
        setValues(initialValues);
      }
    } catch {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async (brandValue: string) => {
    if (!brandValue) {
      setLovs((prevLovs) => ({ ...prevLovs, model: {} }));
      return;
    }
    try {
      const { data: response } = await fetchModelsByBrand({
        variables: { modelId: brandValue },
      });
      const modelArray = response?.Insurance?.lookups?.models || [];
      const modelRecord = lookupListAsRecordObject(modelArray);
      setLovs((prevLovs) => ({
        ...prevLovs,
        model: modelRecord,
      }));
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const transliterateNameOnLicense = async (
    enTxt: string,
    formValues: Record<string, any>
  ) => {
    if (!enTxt) return;
    setLoadingTransliteration(true);
    try {
      const response = await DataService.get(
        `/api/prx/transliterate.qcri.org/en2ar/${enTxt}`
      );
      if (response.ok) {
        const result = await response.json();
        formValues.nameOnLicenseAr = result.results;
      } else {
        formValues.nameOnLicenseAr = enTxt;
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      }
    } catch {
      formValues.nameOnLicenseAr = enTxt;
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setLoadingTransliteration(false);
    }
  };

  const onChange = async (
    fieldName: string,
    fieldValue: any,
    sectionIndex: number,
    newValues: Record<string, any>
  ) => {
    if (fieldName === 'make') {
      await loadModels(fieldValue);
      newValues.model = '';
    }
    setValues(newValues);
  };

  const onCustomBlur = async (
    fieldName: string,
    values: Record<string, any>,
    errors: Record<string, any>
  ) => {
    if (fieldName === 'nameOnLicenseEn') {
      await transliterateNameOnLicense(values.nameOnLicenseEn, values);
    }
    setValues(values);
    return { values, errors };
  };

  const submitForm = async (formValues: Record<string, string>) => {
    setFormDisabled(true);
    setSubmitButtonState('loading');
    try {
      const [formData] = normalizeDynamicFormDrawerValues(
        drawerSections(lovs, values, generalData.line.externalCode),
        formValues
      );

      const variables = {
        entityId: params.id,
        vehicleDetailInputs: {
          nameOnLicenseEn: formData.nameOnLicenseEn,
          nameOnLicenseAr: formData.nameOnLicenseAr,
          vignetteCode: formData.vignetteCode || null,
          vignette: Number(formData.vignette) || null,
          plateCode: formData.plateCode,
          plateNumber: formData.plateNumber,
          usageType: formData.usageType,
          bodyType: formData.bodyType,
          engineType: formData.engineType,
          make: formData.make,
          model: formData.model,
          yearOfMake: Number(formData.yearOfMake) || null,
          engine: formData.engine,
          weight: Number(formData.weight) || null,
          engineSize: Number(formData.engineSize) || null,
          seatingCapacity: Number(formData.seatingCapacity) || null,
          chassis: formData.chassis,
          horsePower: Number(formData.horsePower) || null,
          carValue: formData.carValue || null,
          color: formData.color || null,
        },
        depreciationYears: Number(formData.depreciationYears) || null,
        agencyRepairYear: Number(formData.agencyRepairYear) || null,
      };
      const { errors } = await saveMotorDetails({ variables });
      if (isEmpty(errors)) {
        toast.success(
          <ToastSuccessMessage>
            Motor details updated successfully!
          </ToastSuccessMessage>
        );
        setSubmitButtonState('success');
        onSuccess();
        onClose();
      } else {
        setSubmitButtonState(undefined);
        const errorMessage = getError(errors);
        toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      }
    } catch (error) {
      toast.error(
        <ToastErrorMessage>{getError({ errors: error })}</ToastErrorMessage>
      );
      setSubmitButtonState(undefined);
    } finally {
      setFormDisabled(false);
    }
  };

  const inputForm = useMemo(() => {
    const sections = drawerSections(lovs, data, generalData.line.externalCode);
    const inputs = sections[0].inputs;
    if (lovs?.model && inputs.model) {
      const modelRecord = Object.keys(lovs.model).reduce(
        (acc, code) => {
          acc[code] = lovs.model[code];
          return acc;
        },
        {} as Record<string, string>
      );
      (inputs.model as ISelectFormFieldProps).selectOptions = modelRecord;
      inputs.model.value = values.model;
    }
    Object.keys(inputs).forEach((key) => {
      if (key !== 'model') {
        inputs[key].value = values[key];
      }
    });
    return sections;
  }, [lovs, data, values]);

  useEffect(() => {
    if (open) {
      initialize();
    }
  }, [open, data]);

  const isDisabled = formDisabled || loadingModels || loadingTransliteration;

  return (
    <DynamicFormDrawer
      title="Modify Motor Details"
      isOpen={open}
      onClose={onClose}
      loading={loading}
      sections={inputForm}
      onSubmit={submitForm}
      onChange={onChange}
      onCustomBlur={onCustomBlur}
      submitButtonState={submitButtonState}
      disableForm={isDisabled}
    />
  );
};

export default MotorDetailsDrawer;
